import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import { i18n } from "@/i18n";
import { StorageHelper } from "@/utils/StorageHelper";
import { StorageName } from "@/utils/StorageHelper/constants";
import { AppLanguage, DEFAULT_APP_LANGUAGE, SUPPORTED_APP_LANGUAGES } from "./constants";

const isAppLanguage = (value: unknown): value is AppLanguage =>
    SUPPORTED_APP_LANGUAGES.includes(value as AppLanguage);

/**
 * Single source of truth for the app language. Drives the vue-i18n locale (and with it the
 * `Accept-Language` header, see `src/api/modules/base.ts`, and luv-ui-library's `locale` prop
 * via `useAppLanguage`), the `<html lang>` attribute, and is persisted across sessions.
 */
export const useAppLanguageStore = defineStore("appLanguage", () => {
    const { read, write } = new StorageHelper(StorageName.AppLanguage);

    const storedLanguage = read<string>();
    const language = ref<AppLanguage>(isAppLanguage(storedLanguage) ? storedLanguage : DEFAULT_APP_LANGUAGE);

    // ISO 639-1 code ("en", "de") as used by `<html lang>` and the Accept-Language header.
    const languageCode = computed(() => language.value.split("_")[0] ?? "en");

    const setLanguage = (newLanguage: AppLanguage) => {
        if (!isAppLanguage(newLanguage)) return;

        language.value = newLanguage;
    };

    const reset = () => setLanguage(DEFAULT_APP_LANGUAGE);

    watch(language, (value) => {
        i18n.global.locale = value;
        document.documentElement.lang = languageCode.value;
        write(value);
    }, { immediate: true });

    return {
        language,
        languageCode,
        supportedLanguages: SUPPORTED_APP_LANGUAGES,
        setLanguage,
        reset
    };
});
